import React, { useEffect, useState } from "react";
import { Loader2, CheckCircle2, AlertCircle, KeyRound, Globe, Send, Upload, Smartphone, Copy, Plus, Trash2, Layers, X, Zap } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { getFcmToken } from "../../lib/fcm";

/**
 * Super Admin — Firebase Cloud Messaging configuration.
 *
 * Single page where the super admin pastes:
 *   1. Service account JSON (private — used by backend to mint OAuth tokens)
 *   2. Web App config (public — exposed to storefront so Firebase Web SDK
 *      can initializeApp + getToken({vapidKey}))
 *   3. Web Push VAPID public key (from Cloud Messaging → Web Push certs)
 *
 * Also has a Test Send card to verify the service account works against
 * a real device token before any tenant traffic relies on it.
 */
export default function SuperAdminFirebase() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  // Service account form
  const [saJson, setSaJson] = useState("");
  const [savingSa, setSavingSa] = useState(false);

  // Web config form
  const [web, setWeb] = useState({
    apiKey: "", authDomain: "", projectId: "", storageBucket: "",
    messagingSenderId: "", appId: "", vapidPublicKey: "",
  });
  const [savingWeb, setSavingWeb] = useState(false);

  // Test send
  const [testToken, setTestToken] = useState("");
  const [testing, setTesting] = useState(false);
  const [grabbingToken, setGrabbingToken] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/superadmin/firebase/status");
      setStatus(data);
      // Pre-fill the web form with the parts we already saved so the
      // super admin doesn't lose their work if they navigate away.
      if (data.web_config?.projectId) {
        setWeb((prev) => ({
          ...prev,
          projectId: data.web_config.projectId || "",
          messagingSenderId: data.web_config.messagingSenderId || "",
          appId: data.web_config.appId || "",
        }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load Firebase status");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const saveServiceAccount = async () => {
    setSavingSa(true);
    try {
      const { data } = await api.put("/superadmin/firebase/service-account", {
        service_account_json: saJson,
      });
      toast.success(`Service account saved — project ${data.project_id}`);
      setSaJson("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingSa(false);
    }
  };

  const saveWebConfig = async () => {
    setSavingWeb(true);
    try {
      await api.put("/superadmin/firebase/web-config", web);
      toast.success("Web config saved — frontend can now register FCM tokens");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSavingWeb(false);
    }
  };

  const doTestSend = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/superadmin/firebase/test-send", {
        device_token: testToken.trim(),
        title: "FCM test from KalkiBazaar",
        body: "If you see this, your Firebase config is wired up correctly.",
      });
      toast.success(`Sent: ${data.name || "ok"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test send failed");
    } finally {
      setTesting(false);
    }
  };

  /**
   * Generates an FCM token in THIS browser so the super admin can test the
   * Firebase config without hunting for a token in DevTools. Requires
   * Notification permission — we request it inline so the prompt appears
   * right after the click.
   */
  const grabTokenOnThisDevice = async () => {
    setGrabbingToken(true);
    try {
      if (typeof Notification === "undefined") {
        throw new Error("This browser doesn't support web notifications");
      }
      if (Notification.permission === "denied") {
        throw new Error("Notifications are blocked for this site — enable them in the browser settings and reload");
      }
      if (Notification.permission !== "granted") {
        const perm = await Notification.requestPermission();
        if (perm !== "granted") {
          throw new Error("Notification permission was not granted");
        }
      }
      // sw must be registered before getToken — register it if it isn't.
      if ("serviceWorker" in navigator) {
        try { await navigator.serviceWorker.register("/sw.js"); }
        catch { /* may already be registered */ }
      }
      const token = await getFcmToken();
      if (!token) {
        throw new Error("Firebase Web SDK returned no token. Check that Web config + VAPID are saved above.");
      }
      setTestToken(token);
      toast.success("FCM token captured — click Send test to verify delivery");
    } catch (e) {
      toast.error(e?.message || "Could not generate FCM token");
    } finally {
      setGrabbingToken(false);
    }
  };

  const copyTestToken = async () => {
    if (!testToken) return;
    try {
      await navigator.clipboard.writeText(testToken);
      toast.success("Token copied");
    } catch {
      toast.error("Couldn't copy — select the field manually");
    }
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    try {
      JSON.parse(text); // sanity
      setSaJson(text);
    } catch (err) {
      toast.error("That file isn't valid JSON");
    }
    e.target.value = "";
  };

  if (loading) {
    return <div className="px-6 py-20 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="super-firebase-page">
      <div>
        <div className="overline mb-2">Platform · Push</div>
        <h1 className="font-display text-4xl mb-2">Firebase Cloud Messaging</h1>
        <p className="text-sm text-[hsl(var(--kb-muted))] max-w-2xl">
          One Firebase project powers push for every tenant on the platform. Paste the credentials below — they're stored in the database, not env vars, so you can rotate without redeploying. The existing Web Push channel keeps working until you switch traffic over.
        </p>
      </div>

      {/* Status pill */}
      <div className="flex flex-wrap gap-3" data-testid="super-firebase-status">
        <StatusBadge ok={status?.service_account?.configured} label="Service account" detail={status?.service_account?.project_id} />
        <StatusBadge ok={status?.web_config?.configured} label="Web App config" detail={status?.web_config?.projectId} />
        <StatusBadge ok={status?.vapid_public_key_set} label="VAPID public key" />
        <StatusBadge ok={status?.is_fcm_configured} label="FCM ready" />
      </div>

      {/* Service account */}
      <section className="border border-[hsl(var(--kb-border))] bg-white p-5" data-testid="super-firebase-sa">
        <h2 className="font-display text-xl mb-2 flex items-center gap-2"><KeyRound size={18} /> Service account (private)</h2>
        <p className="text-xs text-[hsl(var(--kb-muted))] mb-4">
          Firebase Console → Project Settings → <b>Service Accounts</b> → <i>Generate new private key</i>. Paste the entire JSON below or upload the file.
          {status?.service_account?.client_email && (
            <span className="block mt-1 text-[11px]">Currently: <code>{status.service_account.client_email}</code></span>
          )}
        </p>
        <label className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-[hsl(var(--kb-border))] hover:bg-[hsl(var(--kb-surface-alt))] mb-2 cursor-pointer" data-testid="super-firebase-sa-pick">
          <Upload className="w-3 h-3" /> Upload .json
          <input type="file" accept="application/json,.json" className="hidden" onChange={onFilePicked} />
        </label>
        <textarea
          rows={8}
          spellCheck={false}
          placeholder='{"type":"service_account","project_id":"...","private_key":"-----BEGIN PRIVATE KEY-----\n..."}'
          value={saJson}
          onChange={(e) => setSaJson(e.target.value)}
          className="kb-input font-mono text-xs"
          data-testid="super-firebase-sa-textarea"
        />
        <div className="mt-3 flex justify-end">
          <button
            onClick={saveServiceAccount}
            disabled={savingSa || !saJson.trim()}
            className="kb-btn-primary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5"
            data-testid="super-firebase-sa-save"
          >
            {savingSa && <Loader2 className="w-3 h-3 animate-spin" />}
            Save service account
          </button>
        </div>
      </section>

      {/* Web app config */}
      <section className="border border-[hsl(var(--kb-border))] bg-white p-5" data-testid="super-firebase-web">
        <h2 className="font-display text-xl mb-2 flex items-center gap-2"><Globe size={18} /> Web App config (public)</h2>
        <p className="text-xs text-[hsl(var(--kb-muted))] mb-4">
          Firebase Console → Project Settings → <b>Your apps</b> → select the Web app → copy values from the SDK setup snippet.
          The VAPID public key is at <b>Cloud Messaging</b> → <b>Web Push certificates</b>.
        </p>
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="API key" value={web.apiKey} onChange={(v) => setWeb({ ...web, apiKey: v })} testid="super-firebase-web-apiKey" />
          <TextField label="Auth domain" value={web.authDomain} onChange={(v) => setWeb({ ...web, authDomain: v })} placeholder="demo.firebaseapp.com" testid="super-firebase-web-authDomain" />
          <TextField label="Project ID" value={web.projectId} onChange={(v) => setWeb({ ...web, projectId: v })} testid="super-firebase-web-projectId" />
          <TextField label="Storage bucket" value={web.storageBucket} onChange={(v) => setWeb({ ...web, storageBucket: v })} placeholder="demo.appspot.com" testid="super-firebase-web-storageBucket" />
          <TextField label="Messaging sender ID" value={web.messagingSenderId} onChange={(v) => setWeb({ ...web, messagingSenderId: v })} testid="super-firebase-web-messagingSenderId" />
          <TextField label="App ID" value={web.appId} onChange={(v) => setWeb({ ...web, appId: v })} placeholder="1:1234:web:abcd" testid="super-firebase-web-appId" />
          <div className="sm:col-span-2">
            <TextField label="Web Push VAPID public key" value={web.vapidPublicKey} onChange={(v) => setWeb({ ...web, vapidPublicKey: v })} placeholder="B…44 chars…" testid="super-firebase-web-vapid" />
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <button
            onClick={saveWebConfig}
            disabled={savingWeb || !web.apiKey || !web.projectId || !web.appId || !web.vapidPublicKey}
            className="kb-btn-primary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5"
            data-testid="super-firebase-web-save"
          >
            {savingWeb && <Loader2 className="w-3 h-3 animate-spin" />}
            Save web config
          </button>
        </div>
      </section>

      {/* Test send */}
      <section className="border border-[hsl(var(--kb-border))] bg-white p-5" data-testid="super-firebase-test">
        <h2 className="font-display text-xl mb-2 flex items-center gap-2"><Send size={18} /> Test send</h2>
        <p className="text-xs text-[hsl(var(--kb-muted))] mb-3">
          Send a one-off notification to a specific FCM device token. Tap <b>Get token on this device</b> to generate one from this browser, or paste a token captured from another device (DevTools → <code>localStorage.getItem('kb_fcm_token')</code>).
        </p>
        <div className="mb-3">
          <button
            onClick={grabTokenOnThisDevice}
            disabled={grabbingToken || !status?.web_config?.configured || !status?.vapid_public_key_set}
            className="kb-btn-secondary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5"
            data-testid="super-firebase-grab-token"
            title={
              !status?.web_config?.configured || !status?.vapid_public_key_set
                ? "Save the Web config and VAPID key first"
                : "Ask this browser for a fresh FCM token"
            }
          >
            {grabbingToken ? <Loader2 className="w-3 h-3 animate-spin" /> : <Smartphone className="w-3 h-3" />}
            Get token on this device
          </button>
        </div>
        <div className="flex gap-2">
          <input
            value={testToken}
            onChange={(e) => setTestToken(e.target.value)}
            placeholder="cZ3F…device token…"
            className="kb-input font-mono text-xs flex-1"
            data-testid="super-firebase-test-token"
          />
          <button
            type="button"
            onClick={copyTestToken}
            disabled={!testToken}
            className="px-2 border border-[hsl(var(--kb-border))] text-xs hover:bg-[hsl(var(--kb-surface-alt))] disabled:opacity-40"
            title="Copy token"
            data-testid="super-firebase-test-copy"
          >
            <Copy className="w-3 h-3" />
          </button>
          <button
            onClick={doTestSend}
            disabled={testing || !testToken.trim() || !status?.is_fcm_configured}
            className="kb-btn-primary !py-1.5 !px-4 !text-xs inline-flex items-center gap-1.5 whitespace-nowrap"
            data-testid="super-firebase-test-send"
          >
            {testing && <Loader2 className="w-3 h-3 animate-spin" />}
            Send test
          </button>
        </div>
      </section>

      <FirebaseShardsSection />
    </div>
  );
}

function StatusBadge({ ok, label, detail }) {
  return (
    <div
      className={
        "inline-flex items-center gap-2 px-3 py-1.5 text-xs border " +
        (ok
          ? "border-green-200 bg-green-50 text-green-800"
          : "border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] text-[hsl(var(--kb-muted))]")
      }
    >
      {ok ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
      <span className="font-medium">{label}</span>
      {detail && <code className="text-[10px] opacity-75">{detail}</code>}
    </div>
  );
}

function TextField({ label, value, onChange, placeholder, testid }) {
  return (
    <label className="text-xs">
      <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))] mb-1">{label}</div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="kb-input font-mono"
        data-testid={testid}
      />
    </label>
  );
}

// ---------------------------------------------------------------------------
// Sharded Firebase projects — multi-project shard manager for Capacitor.
//
// Each project has a cap (default 30, Firebase's per-project Android app
// limit). New tenants are auto-assigned to the shard with the most free
// slots on first build. When a shard fills up, add another one.
// ---------------------------------------------------------------------------

function FirebaseShardsSection() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/firebase/projects");
      setProjects(data.projects || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load shards");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const handleRemove = async (project) => {
    if (!window.confirm(`Remove shard "${project.project_id}"? This will fail if any tenant is still assigned to it.`)) return;
    setRemovingId(project.id);
    try {
      await api.delete(`/superadmin/firebase/projects/${encodeURIComponent(project.id)}`);
      toast.success(`Removed ${project.project_id}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remove failed");
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <section
      className="border border-[hsl(var(--kb-border))] bg-white p-5"
      data-testid="super-firebase-shards"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="font-display text-xl mb-1 flex items-center gap-2">
            <Layers size={18} /> Firebase shards (Capacitor multi-tenant)
          </h2>
          <p className="text-xs text-[hsl(var(--kb-muted))] max-w-2xl">
            Firebase caps each project at 30 Android apps. Add a new project shard whenever existing ones fill up. Tenants are auto-assigned to the shard with the most free slots on their first Capacitor build.
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="kb-btn-primary !py-1.5 !px-3 !text-xs inline-flex items-center gap-1.5 whitespace-nowrap"
          data-testid="super-firebase-shards-add"
        >
          <Plus className="w-3 h-3" /> Add project
        </button>
      </div>

      {loading ? (
        <div className="py-8 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>
      ) : projects.length === 0 ? (
        <div className="py-8 text-center text-xs text-[hsl(var(--kb-muted))]" data-testid="super-firebase-shards-empty">
          No shards configured yet. The Capacitor build script will fail to ship push notifications until you add at least one.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3" data-testid="super-firebase-shards-list">
          {projects.map((p) => (
            <ShardCard
              key={p.id}
              project={p}
              onRemove={() => handleRemove(p)}
              removing={removingId === p.id}
            />
          ))}
        </div>
      )}

      {showAdd && (
        <AddShardDialog
          onClose={() => setShowAdd(false)}
          onAdded={() => { setShowAdd(false); refresh(); }}
        />
      )}
    </section>
  );
}

function ShardCard({ project, onRemove, removing }) {
  const [showTenants, setShowTenants] = useState(false);
  const pct = project.max_apps > 0
    ? Math.min(100, Math.round((project.app_count / project.max_apps) * 100))
    : 0;
  const full = project.available_slots <= 0;
  return (
    <div
      className="border border-[hsl(var(--kb-border))] p-3 bg-[hsl(var(--kb-surface-alt))]"
      data-testid={`super-firebase-shard-${project.project_id}`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="font-mono text-xs truncate" title={project.project_id}>{project.project_id}</div>
          <div className="text-[10px] text-[hsl(var(--kb-muted))] truncate" title={project.client_email}>
            {project.client_email || "—"}
          </div>
        </div>
        <button
          onClick={onRemove}
          disabled={removing || project.app_count > 0}
          title={project.app_count > 0 ? "Cannot remove — tenants still assigned" : "Remove shard"}
          className="p-1 hover:bg-white border border-transparent hover:border-[hsl(var(--kb-border))] disabled:opacity-30 disabled:cursor-not-allowed"
          data-testid={`super-firebase-shard-remove-${project.project_id}`}
        >
          {removing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
        </button>
      </div>

      <div className="flex items-center justify-between text-[11px] mb-1">
        <span className="text-[hsl(var(--kb-muted))]">Capacity</span>
        <span className={full ? "text-red-700 font-medium" : "font-medium"}>
          {project.app_count} / {project.max_apps}
        </span>
      </div>
      <div className="h-1.5 bg-white border border-[hsl(var(--kb-border))] overflow-hidden">
        <div
          className={full ? "h-full bg-red-500" : "h-full bg-green-500"}
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
        <Tag ok={project.web_config_configured} label="Web config" />
        <Tag ok={project.vapid_key_set} label="VAPID key" />
      </div>

      {project.app_count > 0 && (
        <button
          onClick={() => setShowTenants((v) => !v)}
          className="mt-3 text-[10px] underline text-[hsl(var(--kb-muted))] hover:text-[hsl(var(--kb-text))]"
          data-testid={`super-firebase-shard-manage-${project.project_id}`}
        >
          {showTenants ? "Hide tenants" : `Manage ${project.app_count} tenant${project.app_count === 1 ? "" : "s"}`}
        </button>
      )}
      {showTenants && (
        <ShardTenantsManager shardId={project.id} projectIdLabel={project.project_id} />
      )}
    </div>
  );
}

function ShardTenantsManager({ shardId, projectIdLabel }) {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openTenantId, setOpenTenantId] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/superadmin/firebase/projects/${encodeURIComponent(shardId)}/tenants`);
      setTenants(data.tenants || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load tenants");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [shardId]);

  if (loading) {
    return <div className="mt-2 py-3 text-center"><Loader2 className="w-3 h-3 animate-spin inline" /></div>;
  }
  if (tenants.length === 0) {
    return <div className="mt-2 text-[10px] text-[hsl(var(--kb-muted))]">No tenants on this shard yet.</div>;
  }
  return (
    <div className="mt-3 space-y-1.5" data-testid={`super-firebase-shard-tenants-${projectIdLabel}`}>
      {tenants.map((t) => (
        <TenantAssetRow
          key={t.tenant_id}
          tenant={t}
          open={openTenantId === t.tenant_id}
          onToggle={() => setOpenTenantId(openTenantId === t.tenant_id ? null : t.tenant_id)}
          onChanged={refresh}
        />
      ))}
    </div>
  );
}

function TenantAssetRow({ tenant, open, onToggle, onChanged }) {
  return (
    <div className="border border-[hsl(var(--kb-border))] bg-white" data-testid={`super-firebase-tenant-row-${tenant.slug}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-2 px-2 py-1.5 text-left hover:bg-[hsl(var(--kb-surface-alt))]"
      >
        <div className="min-w-0">
          <div className="text-[11px] font-medium truncate">{tenant.name || tenant.slug}</div>
          <div className="text-[9px] font-mono text-[hsl(var(--kb-muted))] truncate" title={tenant.app_id}>{tenant.app_id}</div>
        </div>
        <div className="flex items-center gap-1 text-[9px] whitespace-nowrap">
          <PlatformTag platform="Android" ok={tenant.android_uploaded} />
          <PlatformTag platform="iOS" ok={tenant.ios_uploaded} />
        </div>
      </button>
      {open && <TenantAssetUploader tenant={tenant} onChanged={onChanged} />}
    </div>
  );
}

function PlatformTag({ platform, ok }) {
  return (
    <span
      className={
        "inline-flex items-center gap-0.5 px-1 py-0.5 border " +
        (ok
          ? "border-green-200 bg-green-50 text-green-800"
          : "border-[hsl(var(--kb-border))] bg-white text-[hsl(var(--kb-muted))]")
      }
    >
      {ok ? <CheckCircle2 className="w-2 h-2" /> : <AlertCircle className="w-2 h-2" />}
      {platform}
    </span>
  );
}

function TenantAssetUploader({ tenant, onChanged }) {
  const [gsJson, setGsJson] = useState("");
  const [plistXml, setPlistXml] = useState("");
  const [saving, setSaving] = useState(false);
  const [provisioning, setProvisioning] = useState(false);

  const pickJson = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    try { JSON.parse(text); setGsJson(text); }
    catch { toast.error("That file isn't valid JSON"); }
    e.target.value = "";
  };
  const pickPlist = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    if (!text.includes("<plist")) {
      toast.error("That file doesn't look like a .plist");
      e.target.value = "";
      return;
    }
    setPlistXml(text);
    e.target.value = "";
  };

  const submit = async () => {
    if (!gsJson.trim() && !plistXml.trim()) {
      toast.error("Pick at least one file to upload");
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      if (gsJson.trim()) payload.google_services_json = gsJson;
      if (plistXml.trim()) payload.google_service_info_plist = plistXml;
      await api.put(`/superadmin/tenants/${tenant.tenant_id}/firebase/assets`, payload);
      toast.success(`Saved Firebase assets for ${tenant.slug}`);
      setGsJson("");
      setPlistXml("");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm(`Clear both Firebase assets for ${tenant.slug}? Their native build will lose push until reuploaded.`)) return;
    try {
      await api.delete(`/superadmin/tenants/${tenant.tenant_id}/firebase/assets`);
      toast.success(`Cleared ${tenant.slug} assets`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    }
  };

  const handleProvision = async () => {
    setProvisioning(true);
    try {
      await api.post(`/superadmin/tenants/${tenant.tenant_id}/firebase/provision`);
      toast.success(`Provisioned Firebase apps for ${tenant.slug}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Provision failed — check service-account IAM role");
    } finally {
      setProvisioning(false);
    }
  };

  return (
    <div className="border-t border-[hsl(var(--kb-border))] p-2 space-y-2 bg-[hsl(var(--kb-surface-alt))]" data-testid={`super-firebase-tenant-uploader-${tenant.slug}`}>
      <button
        onClick={handleProvision}
        disabled={provisioning}
        className="w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-[10px] bg-[hsl(var(--kb-primary))] text-white hover:opacity-90 disabled:opacity-50"
        data-testid={`super-firebase-tenant-${tenant.slug}-provision`}
        title="Use the shard's service account to create both apps in Firebase and download configs automatically"
      >
        {provisioning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
        Auto-provision Android &amp; iOS in Firebase
      </button>
      <div className="flex items-center gap-2 text-[9px] text-[hsl(var(--kb-muted))]">
        <span className="flex-1 border-t border-[hsl(var(--kb-border))]"></span>
        <span>or upload manually</span>
        <span className="flex-1 border-t border-[hsl(var(--kb-border))]"></span>
      </div>
      <p className="text-[10px] text-[hsl(var(--kb-muted))]">
        Download from Firebase Console → Project Settings → Your apps after adding the Android app (<code>{tenant.app_id}</code>) and iOS app (<code>{tenant.app_id}</code>).
      </p>

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[9px] uppercase tracking-wider text-[hsl(var(--kb-muted))]">Android — google-services.json</span>
          <label className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] border border-[hsl(var(--kb-border))] bg-white hover:bg-[hsl(var(--kb-surface-alt))] cursor-pointer">
            <Upload className="w-2.5 h-2.5" /> Pick file
            <input type="file" accept=".json,application/json" className="hidden"
              onChange={pickJson}
              data-testid={`super-firebase-tenant-${tenant.slug}-android-pick`} />
          </label>
        </div>
        {gsJson && <div className="text-[9px] text-green-700 font-mono truncate">✓ {gsJson.length} bytes selected</div>}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-[9px] uppercase tracking-wider text-[hsl(var(--kb-muted))]">iOS — GoogleService-Info.plist</span>
          <label className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] border border-[hsl(var(--kb-border))] bg-white hover:bg-[hsl(var(--kb-surface-alt))] cursor-pointer">
            <Upload className="w-2.5 h-2.5" /> Pick file
            <input type="file" accept=".plist,text/xml,application/xml" className="hidden"
              onChange={pickPlist}
              data-testid={`super-firebase-tenant-${tenant.slug}-ios-pick`} />
          </label>
        </div>
        {plistXml && <div className="text-[9px] text-green-700 font-mono truncate">✓ {plistXml.length} bytes selected</div>}
      </div>

      <div className="flex items-center justify-between gap-2 pt-1">
        <button
          onClick={handleClear}
          disabled={!tenant.android_uploaded && !tenant.ios_uploaded}
          className="text-[9px] text-red-700 hover:underline disabled:opacity-30 disabled:no-underline"
          data-testid={`super-firebase-tenant-${tenant.slug}-clear`}
        >
          Clear both
        </button>
        <button
          onClick={submit}
          disabled={saving || (!gsJson.trim() && !plistXml.trim())}
          className="kb-btn-primary !py-1 !px-2 !text-[10px] inline-flex items-center gap-1 disabled:opacity-50"
          data-testid={`super-firebase-tenant-${tenant.slug}-save`}
        >
          {saving && <Loader2 className="w-2.5 h-2.5 animate-spin" />}
          Upload
        </button>
      </div>
    </div>
  );
}

function Tag({ ok, label }) {
  return (
    <span
      className={
        "inline-flex items-center gap-1 px-1.5 py-0.5 border " +
        (ok
          ? "border-green-200 bg-green-50 text-green-800"
          : "border-[hsl(var(--kb-border))] bg-white text-[hsl(var(--kb-muted))]")
      }
    >
      {ok ? <CheckCircle2 className="w-2.5 h-2.5" /> : <AlertCircle className="w-2.5 h-2.5" />}
      {label}
    </span>
  );
}

function AddShardDialog({ onClose, onAdded }) {
  const [saJson, setSaJson] = useState("");
  const [maxApps, setMaxApps] = useState(30);
  const [saving, setSaving] = useState(false);

  const pickFile = async (e, setter) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    try {
      JSON.parse(text);
      setter(text);
    } catch {
      toast.error("That file isn't valid JSON");
    }
    e.target.value = "";
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        service_account_json: saJson,
        max_apps: Number(maxApps) || 30,
      };
      const { data } = await api.post("/superadmin/firebase/projects", payload);
      toast.success(`Added shard ${data.project_id}`);
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="super-firebase-shards-add-dialog">
      <div className="bg-white border border-[hsl(var(--kb-border))] max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="font-display text-lg">Add Firebase project shard</h3>
            <p className="text-xs text-[hsl(var(--kb-muted))]">
              Paste or upload the service account JSON. Per-tenant <code>google-services.json</code> and <code>GoogleService-Info.plist</code> are uploaded later — one per tenant, after you add the Android &amp; iOS apps for them in Firebase Console.
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-[hsl(var(--kb-surface-alt))]" data-testid="super-firebase-shards-add-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))]">
                Service account JSON <span className="text-red-600">*</span>
              </label>
              <label className="inline-flex items-center gap-1 px-2 py-1 text-[10px] border border-[hsl(var(--kb-border))] hover:bg-[hsl(var(--kb-surface-alt))] cursor-pointer">
                <Upload className="w-3 h-3" /> Upload
                <input type="file" accept=".json,application/json" className="hidden"
                  onChange={(e) => pickFile(e, setSaJson)}
                  data-testid="super-firebase-shards-sa-pick" />
              </label>
            </div>
            <textarea
              value={saJson}
              onChange={(e) => setSaJson(e.target.value)}
              placeholder='{"type":"service_account","project_id":"…","private_key":"…"}'
              rows={4}
              className="kb-input font-mono text-[10px] w-full"
              data-testid="super-firebase-shards-sa-json"
            />
          </div>

          <label className="block text-xs">
            <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--kb-muted))] mb-1">
              Max apps (Firebase default = 30)
            </div>
            <input
              type="number"
              min={1}
              max={200}
              value={maxApps}
              onChange={(e) => setMaxApps(e.target.value)}
              className="kb-input font-mono w-32"
              data-testid="super-firebase-shards-max-apps"
            />
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-xs border border-[hsl(var(--kb-border))] hover:bg-[hsl(var(--kb-surface-alt))]"
            data-testid="super-firebase-shards-add-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving || !saJson.trim()}
            className="kb-btn-primary !py-1.5 !px-4 !text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="super-firebase-shards-add-submit"
          >
            {saving && <Loader2 className="w-3 h-3 animate-spin" />}
            Add shard
          </button>
        </div>
      </div>
    </div>
  );
}
